import { Component, OnInit, ViewEncapsulation, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { DialogComponent } from "@components/layouts/dialog/dialog.component";
import {
  IGetSignerDetails,
  IGetSignerDetailsTag,
  IResendNotificationRequest,
  ISigner,
} from "@modals/api.modal";
import { EnumRequestStatus } from "@modals/app.modal";
import { Constants } from "@config/constants";
import { AppTokenResolverService } from "@services/app-token-resolver.service";
import { HttpService } from "@services/http.service";
import { LanguageService } from "@services/language.service";
import { ToastService } from "@services/toast.service";
import { Messages } from "@config/messages";
import { EMPTY, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

export interface IRequestDetailsDialogData {
  signRequestId: string;
  appToken?: string;
  appName?: string;
  requestStatus?: string;
  /** Signers from SignRequest/All — used when GetSignerDetails is unavailable. */
  fallbackSigners?: ISigner[];
}

@Component({
  selector: "kzn-request-details-dialog",
  standalone: true,
  imports: [DialogComponent, MatProgressSpinnerModule],
  templateUrl: "./request-details-dialog.component.html",
  styleUrl: "./request-details-dialog.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class RequestDetailsDialogComponent implements OnInit {
  private _dialogRef = inject(
    MatDialogRef<RequestDetailsDialogComponent, undefined>
  );
  private _data = inject<IRequestDetailsDialogData>(MAT_DIALOG_DATA);
  private _languageService = inject(LanguageService);
  private _httpService = inject(HttpService);
  private _toastr = inject(ToastService);
  private _appTokenResolver = inject(AppTokenResolverService);

  isLoading = true;
  resendingSignerId = "";
  errorMessage = "";
  details: IGetSignerDetails[] = [];
  private cachedAppToken = "";

  ngOnInit(): void {
    const preset = this._data.appToken?.trim();
    if (preset) {
      this.cachedAppToken = preset;
    }
    this.loadDetails();
  }

  onClose(): void {
    this._dialogRef.close();
  }

  canResendSigner(detail: IGetSignerDetails): boolean {
    if (!this.isResendAllowedForRequest()) return false;
    if (this.hasSignedDate(detail)) return false;
    return !!detail.Id?.trim();
  }

  isResendingSigner(detail: IGetSignerDetails): boolean {
    const id = detail.Id?.trim();
    return !!id && this.resendingSignerId === id;
  }

  isAnyResendInProgress(): boolean {
    return !!this.resendingSignerId;
  }

  onResendSigner(detail: IGetSignerDetails): void {
    if (!this.canResendSigner(detail) || this.isAnyResendInProgress()) return;

    const signRequestId = this._data.signRequestId?.trim();
    const requestSignerId = detail.Id?.trim();
    if (!signRequestId || !requestSignerId) return;

    this.resendingSignerId = requestSignerId;
    this.resolveAppTokenForResend$()
      .pipe(
        switchMap((appToken) => {
          if (!appToken) {
            this.resendingSignerId = "";
            this._toastr.showError(Messages.MSG_ERROR_APP_TOKEN_NOT_FOUND);
            return EMPTY;
          }

          const apiUrl = this._httpService.createEndpoint(
            Constants.API_URI_RESEND_NOTIFICATION
          );
          return this._httpService.post(apiUrl, {
            // appToken,
            signRequestId,
            requestSignerId,
          } satisfies IResendNotificationRequest);
        })
      )
      .subscribe({
        next: () => {
          this.resendingSignerId = "";
          this._toastr.showSuccess(Messages.MSG_SUCCESS_RESEND_NOTIFICATION);
        },
        error: (error) => {
          this.resendingSignerId = "";
          this._httpService.showHttpError(error);
        },
      });
  }

  private resolveAppTokenForResend$() {
    const preset = this._data.appToken?.trim() || this.cachedAppToken;
    if (preset) return of(preset);
    return this._appTokenResolver.resolve$(undefined, this._data.appName).pipe(
      map((token) => {
        if (token) {
          this.cachedAppToken = token;
        }
        return token;
      }),
      catchError(() => of(null))
    );
  }

  formatSignDate(detail: IGetSignerDetails): string {
    const value = detail.SignDate?.trim();
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  }

  t(key: string): string {
    return this._languageService.instant((key || "").toString());
  }

  private isResendAllowedForRequest(): boolean {
    const status = this._data.requestStatus?.trim();
    return (
      status === EnumRequestStatus.OPEN ||
      status === EnumRequestStatus.PARTIAL_SIGNED
    );
  }

  private hasSignedDate(detail: IGetSignerDetails): boolean {
    const value = detail.SignDate;
    if (value == null) return false;
    return String(value).trim() !== "";
  }

  // private loadDetails(): void {
  //   const signRequestId = this._data.signRequestId?.trim();
  //   if (!signRequestId) {
  //     this.isLoading = false;
  //     this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
  //     return;
  //   }

  //   this._appTokenResolver
  //     .resolve$(this._data.appToken, this._data.appName)
  //     .pipe(
  //       switchMap((appToken) => {
  //         if (appToken) {
  //           this.cachedAppToken = appToken;
  //         }
  //         const params: Record<string, string> = { SignRequestID: signRequestId };
  //         if (appToken) {
  //           params["AppToken"] = appToken;
  //         }
  //         return this._httpService.get(
  //           this._httpService.createEndpoint(
  //             Constants.API_URI_SIGN_REQUEST_GET_SIGNER_DETAILS,
  //             undefined,
  //             params
  //           )
  //         );
  //       }),
  //       catchError(() => of(null))
  //     )
  //     .subscribe({
  //       next: (response: unknown) => {
  //         this.isLoading = false;
  //         if (response == null) {
  //           if (!this.applyFallbackSigners()) {
  //             this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
  //           }
  //           return;
  //         }

  //         const res = response as {
  //           Data?: unknown[];
  //           data?: unknown[];
  //           FailRules?: { Msg?: string }[];
  //           failRules?: { msg?: string; Msg?: string }[];
  //         };
  //         const rawItems = res.Data ?? res.data ?? [];
  //         this.details = rawItems
  //           .map((item) => this.normalizeDetails(item))
  //           .filter((item): item is IGetSignerDetails => !!item)
  //           .sort((a, b) => a.Order - b.Order);
  //         if (!this.details.length) {
  //           if (this.applyFallbackSigners()) {
  //             return;
  //           }
  //           this.errorMessage =
  //             res.FailRules?.[0]?.Msg ||
  //             res.failRules?.[0]?.Msg ||
  //             res.failRules?.[0]?.msg ||
  //             Messages.MSG_NO_RECORDS;
  //         }
  //       },
  //       error: (error) => {
  //         this.isLoading = false;
  //         if (!this.applyFallbackSigners()) {
  //           this._httpService.showHttpError(error);
  //           this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
  //         }
  //       },
  //     });
  // }

  private loadDetails(): void {
  const signRequestId = this._data.signRequestId?.trim();
  if (!signRequestId) {
    this.isLoading = false;
    this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
    return;
  }

  this._appTokenResolver
    .resolve$(this._data.appToken, this._data.appName)
    .pipe(
      switchMap(() => {
        
        const params: Record<string, string> = { SignRequestID: signRequestId };
        
        const endpoint = this._httpService.createEndpoint(
          Constants.API_URI_SIGN_REQUEST_GET_SIGNER_DETAILS,
          undefined,
          params
        );
        return this._httpService.get(endpoint);
      }),
      
      catchError((error) => {
        this.isLoading = false;
        if (!this.applyFallbackSigners()) {
          this._httpService.showHttpError(error);
          this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
        }
        return of(null);
      })
    )
    .subscribe({
      next: (response: unknown) => {
        
        if (response == null) {
          this.isLoading = false;
          if (this.errorMessage === '') { 
            if (!this.applyFallbackSigners()) {
              this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
            }
          }
          return;
        }

        const res = response as {
          Data?: unknown[];
          data?: unknown[];
          FailRules?: { Msg?: string }[];
          failRules?: { msg?: string; Msg?: string }[];
        };
        
        const rawItems = res.Data ?? res.data ?? [];
        this.details = rawItems
          .map((item) => this.normalizeDetails(item))
          .filter((item): item is IGetSignerDetails => !!item)
          .sort((a, b) => a.Order - b.Order);

        this.isLoading = false;

        if (!this.details.length) {
          if (this.applyFallbackSigners()) {
            return;
          }
          this.errorMessage =
            res.FailRules?.[0]?.Msg ||
            res.failRules?.[0]?.Msg ||
            res.failRules?.[0]?.msg ||
            Messages.MSG_NO_RECORDS;
        }
      }
    });
}


  private applyFallbackSigners(): boolean {
    const fallback = this._data.fallbackSigners ?? [];
    if (!fallback.length) {
      return false;
    }
    this.details = this.mapFallbackSigners(fallback);
    this.errorMessage = "";
    return true;
  }

  private mapFallbackSigners(signers: ISigner[]): IGetSignerDetails[] {
    return signers
      .map((signer) => ({
        Id: signer.Id?.trim() ?? "",
        SignerName: signer.SignerName?.trim() ?? "",
        SignerEmail: signer.SignerEmail?.trim() ?? "",
        Phone: "",
        Tags: "",
        ParsedTags: [],
        SignerRole: "",
        SignerIP: "",
        Order: signer.Order ?? 0,
        SignDate: signer.SignDate == null ? "" : String(signer.SignDate).trim(),
      }))
      .sort((a, b) => a.Order - b.Order);
  }

  private normalizeDetails(raw: unknown): IGetSignerDetails | null {
    if (!raw || typeof raw !== "object") return null;
    const item = raw as Record<string, unknown>;
    const tags = this.readString(item, "Tags", "tags");
    return {
      Id: this.readString(
        item,
        "Id",
        "id",
        "RequestSignerId",
        "requestSignerId"
      ),
      SignerName: this.readString(item, "SignerName", "signerName"),
      SignerEmail: this.readString(item, "SignerEmail", "signerEmail"),
      Phone: this.readString(item, "Phone", "phone"),
      Tags: tags,
      ParsedTags: this.parseTags(tags),
      SignerRole: this.readString(item, "SignerRole", "signerRole"),
      SignerIP: this.readString(item, "SignerIP", "signerIP"),
      Order: this.readNumber(item, "Order", "order"),
      SignDate: this.readString(item, "SignDate", "signDate", "SignedDate", "signedDate"),
    };
  }

  private parseTags(raw: string): IGetSignerDetailsTag[] {
    const text = raw?.trim();
    if (!text) return [];
    try {
      const parsed = JSON.parse(`[${text}]`) as unknown[];
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((entry) => this.normalizeTag(entry))
        .filter((entry): entry is IGetSignerDetailsTag => !!entry);
    } catch {
      return [];
    }
  }

  private normalizeTag(raw: unknown): IGetSignerDetailsTag | null {
    if (!raw || typeof raw !== "object") return null;
    const item = raw as Record<string, unknown>;
    return {
      Type: this.readString(item, "Type", "type"),
      XLocation: this.readNumber(item, "XLocation", "xLocation"),
      YLocation: this.readNumber(item, "YLocation", "yLocation"),
      Width: this.readNumber(item, "Width", "width"),
      Height: this.readNumber(item, "Height", "height"),
      Text: this.readString(item, "Text", "text"),
      Page: this.readNumber(item, "Page", "page"),
    };
  }

  private readString(
    item: Record<string, unknown>,
    pascalKey: string,
    camelKey: string,
    altPascalKey?: string,
    altCamelKey?: string
  ): string {
    const value =
      item[pascalKey] ??
      item[camelKey] ??
      (altPascalKey ? item[altPascalKey] : undefined) ??
      (altCamelKey ? item[altCamelKey] : undefined);
    return value == null ? "" : String(value).trim();
  }

  private readNumber(
    item: Record<string, unknown>,
    pascalKey: string,
    camelKey: string
  ): number {
    const value = item[pascalKey] ?? item[camelKey];
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  }
}
